import { ActivityType, ActivitySubType } from '@frontend/app/types/globalTypes';

// Filter Options for UI
export const FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: ActivityType.APPLICATION, label: 'Applications' },
  { value: ActivityType.MEMBER_INFO_REQUEST, label: 'Member Info Requests' },
  { value: ActivityType.MARKETPLACE, label: 'Marketplace' },
  { value: ActivityType.STAGE, label: 'Stages' },
  { value: ActivityType.CUSTOM_STAGE, label: 'Custom Stages' },
  { value: ActivityType.LIVE_POST, label: 'Live Posts' },
  { value: ActivityType.CONTENT, label: 'Content' },
  { value: ActivityType.BRIEF, label: 'Briefs' },
  { value: ActivityType.PRODUCT, label: 'Products' },
] as const;

// Activity Interfaces
export interface ActivityMedia {
  type: string;
  url: string;
  liveUploadLink?: string;
}

export interface ActivityAction {
  actionText: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parameters: Record<string, any>;
}

export interface ActivityLog {
  id: number;
  type: ActivityType;
  subType: string;
  programId?: number;
  memberId?: number;
  title: string;
  description: string;
  actions: ActivityAction[];
  media: ActivityMedia[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: Record<string, any>;
  createdDate: Date;
  updatedDate: Date;
}

// Grouped Activity Interface
export interface GroupedActivity {
  id: string; // Combination of date, subtype, and grouping key
  subType: string;
  type: ActivityType;
  count: number;
  activities: ActivityLog[];
  createdDate: Date; // Earliest activity date in the group
  title: string; // Dynamic title for the group
  description: string; // Dynamic description for the group
  groupingKey: string; // Key used for secondary grouping (e.g., 'title', 'member', 'stage')
  groupingValue: string; // Value of the grouping key (e.g., stage name, member name)
}

// Simple grouping configuration
export interface ActivityGroupingConfig {
  subType: string;
  groupTitle: string;
  groupDescription: string;
  shouldGroup: boolean;
  groupBy: 'title' | 'metadata' | 'none'; // How to group within the same subtype
  metadataKey?: string; // Key in metadata to group by (if groupBy is 'metadata')
  minGroupSize?: number;
}

// Helper to create grouping configs
const createGrouping = (
  subType: string,
  title: string,
  description: string,
  groupBy: 'title' | 'metadata' | 'none' = 'none',
  metadataKey?: string,
  minGroupSize: number = 2
): ActivityGroupingConfig => ({
  subType,
  groupTitle: title,
  groupDescription: description,
  shouldGroup: true,
  groupBy,
  metadataKey,
  minGroupSize,
});

// Only specific subtypes are grouped - others display individually
export const ACTIVITY_GROUPING_CONFIG: Record<string, ActivityGroupingConfig> = {
  // Applications - Group these
  [ActivitySubType.APPLICATION_SUBMITTED]: createGrouping(
    ActivitySubType.APPLICATION_SUBMITTED,
    'Applications Submitted',
    '{count} creators applied to campaign'
  ),
  [ActivitySubType.APPLICATION_APPROVED]: createGrouping(
    ActivitySubType.APPLICATION_APPROVED,
    'Application Approved',
    '{userName} approved {count} creators',
    'metadata',
    'userName'
  ),
  [ActivitySubType.APPLICATION_REJECTED]: createGrouping(
    ActivitySubType.APPLICATION_REJECTED,
    'Application Rejected',
    '{userName} rejected {count} creators',
    'metadata',
    'userName'
  ),
  [ActivitySubType.MEMBER_INFO_REQUEST_RECEIVED]: createGrouping(
      ActivitySubType.MEMBER_INFO_REQUEST_RECEIVED,
      'Member Info Request Received',
      '{count} members submitted member information requests',
  ),
  [ActivitySubType.BRIEF_SIGNED]: createGrouping(
    ActivitySubType.BRIEF_SIGNED,
    'Brief Signed',
    '{count} members signed brief',
  ),
  [ActivitySubType.BRIEF_UPDATED]: createGrouping(
    ActivitySubType.BRIEF_UPDATED,
    'Brief Edit Request',
    '{count} members requested edits to brief',
  ),
  [ActivitySubType.CONTENT_SUBMITTED]: createGrouping(
    ActivitySubType.CONTENT_SUBMITTED,
    'Content Submitted',
    '{count} members submitted content for review',
  ),
  [ActivitySubType.CONTENT_AUTO_APPROVED]: createGrouping(
    ActivitySubType.CONTENT_AUTO_APPROVED,
    'Content Auto-Approved',
    '{count} members content automatically approved',
  ),
  [ActivitySubType.CONTENT_APPROVED]: createGrouping(
    ActivitySubType.CONTENT_APPROVED,
    'Content Approved',
    '{count} members content approved',
  ),
  [ActivitySubType.CONTENT_REJECTED]: createGrouping(
    ActivitySubType.CONTENT_REJECTED,
    'Content Rejected',
    '{count} members content rejected',
  ),
  [ActivitySubType.CREATOR_ORDER_REQUEST_CREATED]: createGrouping(
    ActivitySubType.CREATOR_ORDER_REQUEST_CREATED,
    'Product Order Requested',
    '{count} members requested product',
  ),
  [ActivitySubType.PRODUCT_SHIPPED]: createGrouping(
    ActivitySubType.PRODUCT_SHIPPED,
    'Order Shipped',
    'Orders shipped to {count} members',
  ),
  [ActivitySubType.CREATOR_ORDER_REQUEST_APPROVED]: createGrouping(
    ActivitySubType.CREATOR_ORDER_REQUEST_APPROVED,
    'Order Approved',
    '{count} members’ order approved',
  ),
  [ActivitySubType.CREATOR_ORDER_REQUEST_REJECTED]: createGrouping(
    ActivitySubType.CREATOR_ORDER_REQUEST_REJECTED,
    'Order Rejected',
    '{count} members’ order rejected',
  ),
  [ActivitySubType.CREATOR_ORDER_REQUEST_AUTO_APPROVED]: createGrouping(
    ActivitySubType.CREATOR_ORDER_REQUEST_AUTO_APPROVED,
    'Order Auto-Approved',
    '{count} members’ order automatically approved',
  ),
  [ActivitySubType.STAGE_COMPLETED]: createGrouping(
    ActivitySubType.STAGE_COMPLETED,
    'Creators Marked as Completed',
    '{userName} moved {count} members to completed',
    'metadata',
    'userName'
  ),
  // Custom Stages - Group by title to avoid "Completed Completed"
  [ActivitySubType.CUSTOM_STAGE_COMPLETED]: createGrouping(
    ActivitySubType.CUSTOM_STAGE_COMPLETED,
    'Custom Stages Completed',
    '{count} members completed tasks',
    'title' // Group by title
  ),
  // All other subtypes are NOT grouped - they display individually
  // This means: LIVE_POST_UPLOADED, CREATOR_ORDER_REQUEST_*, PRODUCT_SHIPPED,
  // COMMISSION_*, BRIEF_*, MARKETPLACE_*, STAGE_*, CUSTOM_STAGE_STATUS_CHANGED,
  // PROGRAM_MEMBER_*, MEMBER_INFO_REQUEST_RECEIVED will all show individually
};

// Simple helper to check if an activity should be grouped
export const shouldGroupActivity = (subType: string): boolean => {
  const config = ACTIVITY_GROUPING_CONFIG[subType];
  return config ? config.shouldGroup : false;
};
